/**
 * Poll for new orders/messages on admin pages that are NOT the Orders page
 * (Orders has its own richer refresh + print flow).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startMessageAlertSoundLoop,
  startOrderAlertSoundLoop,
  stopMessageAlertSoundLoop,
  stopOrderAlertSoundLoop,
  stopAllAlertSoundLoops,
  requestNotificationPermission,
  showBrowserNotification,
  unlockAdminAudio,
} from '../utils/adminNotifications';
import { adminFetch } from '../lib/adminApi';

const POLL_MS = 30_000;

export interface AdminLiveFlash {
  order: { open: boolean; count: number };
  message: { open: boolean; count: number };
}

export function useAdminLiveAlerts(enabled: boolean, soundEnabled: boolean) {
  const [flash, setFlash] = useState<AdminLiveFlash>({
    order: { open: false, count: 0 },
    message: { open: false, count: 0 },
  });
  const audioRef = useRef<AudioContext | null>(null);
  const lastStatsTotalRef = useRef<number | null>(null);
  const lastContactNewRef = useRef<number | null>(null);

  const dismissOrder = useCallback(() => {
    stopOrderAlertSoundLoop();
    setFlash((f) => ({ ...f, order: { ...f.order, open: false } }));
  }, []);

  const dismissMessage = useCallback(() => {
    stopMessageAlertSoundLoop();
    setFlash((f) => ({ ...f, message: { ...f.message, open: false } }));
  }, []);

  const showOrderFlash = useCallback((count: number) => {
    setFlash((f) => ({ ...f, order: { open: true, count } }));
  }, []);

  const showMessageFlash = useCallback((count: number) => {
    setFlash((f) => ({ ...f, message: { open: true, count } }));
  }, []);

  const poll = useCallback(async () => {
    try {
      const [statsRes, contactRes] = await Promise.all([
        adminFetch('/api/orders/stats').catch(() => null),
        adminFetch('/api/contact/stats').catch(() => null),
      ]);

      if (statsRes?.ok) {
        const statsData = await statsRes.json();
        const statsTotal =
          typeof statsData.total === 'number' && !Number.isNaN(statsData.total)
            ? statsData.total
            : null;
        const prev = lastStatsTotalRef.current;
        if (prev !== null && statsTotal !== null && statsTotal > prev) {
          const delta = statsTotal - prev;
          showOrderFlash(delta);
          if (soundEnabled) {
            if (!audioRef.current) audioRef.current = unlockAdminAudio();
            startOrderAlertSoundLoop(audioRef.current);
          }
          showBrowserNotification(
            '🧁 New Order!',
            `You have ${delta} new order${delta > 1 ? 's' : ''}!`,
          );
        }
        if (statsTotal !== null) lastStatsTotalRef.current = statsTotal;
      }

      if (contactRes?.ok) {
        const contactData = await contactRes.json();
        const newCount = contactData.new ?? 0;
        const prev = lastContactNewRef.current;
        if (prev !== null && newCount > prev) {
          const delta = Math.max(1, newCount - prev);
          showMessageFlash(delta);
          if (soundEnabled) {
            if (!audioRef.current) audioRef.current = unlockAdminAudio();
            startMessageAlertSoundLoop(audioRef.current);
          }
          showBrowserNotification(
            '📩 New contact message',
            `${newCount} new message${newCount > 1 ? 's' : ''}. Open Messages to reply.`,
          );
        }
        lastContactNewRef.current = newCount;
      }
    } catch {
      /* ignore poll errors */
    }
  }, [showMessageFlash, showOrderFlash, soundEnabled]);

  // Tammy turned loop sound off — stop any active chirp immediately.
  useEffect(() => {
    if (!soundEnabled) {
      stopAllAlertSoundLoops();
    }
  }, [soundEnabled]);

  useEffect(() => {
    if (!enabled) return;
    void requestNotificationPermission();
    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [enabled, poll]);

  useEffect(() => {
    const unlock = () => {
      if (!audioRef.current) audioRef.current = unlockAdminAudio();
    };
    document.addEventListener('pointerdown', unlock, true);
    return () => {
      document.removeEventListener('pointerdown', unlock, true);
      stopAllAlertSoundLoops();
    };
  }, []);

  return { flash, dismissOrder, dismissMessage };
}
